import {isZahavOnlyAmount} from '@sollapay/domain';
import {BankAccountIdentifierType} from '@sollapay/enums';
import {useEffect} from 'react';

import {isBankCheckAccountType, payeeHasBankAccount} from '../utils/creditor-account.utils';
import {bankDetailsToIban, ibanToBankDetails} from '../utils/transfer-type.utils';

import type {CreatePayoutFormValues} from '../components/PaymentDetailsStep';
import type {CreditorAccountType, PayeeKind} from '../utils/creditor-account.utils';
import type {BankDetailsValues} from '../utils/transfer-type.utils';
import type {PaymentProviderDTO} from '@sollapay/types';
import type {UseFormGetValues, UseFormSetValue} from 'react-hook-form';

interface UsePayoutTransferRulesParams {
  payeeKind: PayeeKind;
  amount: string;
  accountType: CreditorAccountType;
  isAccountLocked: boolean;
  isCheckEnabled: boolean;
  bankProviders: PaymentProviderDTO[];
  getValues: UseFormGetValues<CreatePayoutFormValues>;
  setValue: UseFormSetValue<CreatePayoutFormValues>;
}

/**
 * The account-type select picks the rail (IBAN → zahav, bank details → masav). At or above the
 * zahav-only threshold masav is off the table, so a bank-details entry is converted to its IBAN
 * rather than discarded. A firm that can pay by check is never forced onto zahav (`isZahavLocked`
 * stays false) — masav is just dropped from its options.
 */
export function usePayoutTransferRules({
  payeeKind,
  amount,
  accountType,
  isAccountLocked,
  isCheckEnabled,
  bankProviders,
  getValues,
  setValue
}: UsePayoutTransferRulesParams) {
  const isZahavOnly = payeeHasBankAccount(payeeKind) && isZahavOnlyAmount(amount);

  useEffect(() => {
    if (!isZahavOnly || accountType !== BankAccountIdentifierType.BANK_DETAILS) return;
    // Unconvertible details clear the IBAN rather than leaving an earlier, hidden one to submit.
    setValue('accountNumber', bankDetailsToIban(getValues()) ?? '', {shouldDirty: true});
    setValue('accountType', BankAccountIdentifierType.IBAN, {shouldDirty: true});
  }, [isZahavOnly, accountType, getValues, setValue]);

  // The flag is polled — if it turns off while a check is selected, the API would reject the payout.
  useEffect(() => {
    if (isCheckEnabled || !isBankCheckAccountType(accountType)) return;
    setValue('accountType', BankAccountIdentifierType.BANK_DETAILS, {shouldDirty: true});
  }, [isCheckEnabled, accountType, setValue]);

  // A locked account is held as an IBAN — re-derive its read-only bank details once providers load.
  useEffect(() => {
    if (!isAccountLocked || accountType !== BankAccountIdentifierType.BANK_DETAILS) return;
    const details = ibanToBankDetails(getValues('accountNumber'), bankProviders);
    if (!details) return;
    for (const [field, value] of Object.entries(details) as [keyof BankDetailsValues, string][]) {
      if (getValues(field) !== value) setValue(field, value, {shouldDirty: true});
    }
  }, [isAccountLocked, accountType, bankProviders, getValues, setValue]);

  return {isZahavOnly, isZahavLocked: isZahavOnly && !isCheckEnabled};
}
